import {
  useEffect,
  useMemo,
  useState,
} from "react";

import CashflowBreakdown from "../../components/director/CashflowBreakdown";
import CashflowMovementChart from "../../components/director/CashflowMovementChart";
import CashflowTable from "../../components/director/CashflowTable";
import DirectorFilters from "../../components/director/DirectorFilters";
import ScopeNotice from "../../components/director/ScopeNotice";
import { useDirectorActions, useDirectorContext } from "@contexts/director";
import FreshnessFooter from "../../components/director/FreshnessFooter";
import DirectorPage from "../../components/director/page/DirectorPage";
import DirectorSidePanel from "../../components/director/page/DirectorSidePanel";
import { cashflowAnswer, cashflowTwelveMonths } from "../../components/director/page/answers";
import readingWarnings from "../../components/director/page/readingWarnings";
import {
  compactCents,
  formatCompetence,
  formatCompetenceShort,
  formatDateTimeCompact,
} from "../../utils/directorFormat";
import { primaryScreen } from "./complementaryPages";

const SCREEN = primaryScreen("cashflow");

const safeDate = (value) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const localCompetence = (value) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(safeDate(value));
  const read = (type) => parts.find((part) => part.type === type)?.value;
  return `${read("year")}-${read("month")}`;
};

const shiftMonth = (competence, delta) => {
  const [year, month] = competence.split("-").map(Number);
  const shifted = new Date(Date.UTC(year, month - 1 + delta, 1));
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, "0")}`;
};

export const defaultCashflowRange = (asOf) => {
  const competence = localCompetence(asOf);
  return { from: shiftMonth(competence, -6), to: shiftMonth(competence, 18) };
};

// Mês de referência dos 12 meses: a data de referência do BE (data dos dados quando estão
// atrasados ou bloqueados) ou, sem ela, o mês corrente em Brasília.
const referenceCompetence = (refreshStatus, resolvedAt) => {
  const reference = refreshStatus?.referenceDate;
  if (typeof reference === "string" && /^\d{4}-\d{2}-\d{2}$/.test(reference)) return reference.slice(0, 7);
  return localCompetence(resolvedAt);
};

const cashflowCards = (totals, onOpenMonth) => {
  if (!totals) return [];
  return [
    { key: "inflow", label: "Entradas 12 meses", cents: totals.inflowCents, tone: "good", note: "Recebido e a receber" },
    { key: "outflow", label: "Saídas 12 meses", cents: totals.outflowCents, tone: "pressure", note: "Pago, contratado e planejado" },
    {
      key: "net",
      label: "Saldo do período",
      cents: totals.netCents,
      tone: totals.netCents < 0 ? "pressure" : "good",
      note: "Entradas menos saídas, sem o saldo bancário",
    },
    totals.worstMonth && {
      key: "worst",
      label: "Pior mês",
      value: formatCompetenceShort(totals.worstMonth),
      tone: totals.worstNetCents < 0 ? "warning" : "neutral",
      note: `${compactCents(totals.worstNetCents)} no mês · ver composição`,
      onClick: () => onOpenMonth(totals.worstMonth),
    },
  ];
};

const CashFlow = () => {
  const state = useDirectorContext();
  const actions = useDirectorActions();
  const {
    selectedCompanyId,
    accessByCompany,
    metadata,
    period,
    filters,
    cashflow,
    cashflowBreakdown,
    cashflowBreakdownError,
    loading,
    error,
    refreshStatus = null,
  } = state;
  // Mês aberto no painel lateral (composição por obra e centro de custo); null = fechado.
  const [selectedCompetence, setSelectedCompetence] = useState(null);
  const [scenario, setScenario] = useState("base");
  const access = accessByCompany[selectedCompanyId] || {};
  const defaultPeriod = useMemo(
    () => defaultCashflowRange(access.resolvedAt),
    [access.resolvedAt],
  );
  const hasPeriod = Boolean(period?.from && period?.to);
  const activePeriod = hasPeriod ? period : defaultPeriod;

  useEffect(() => {
    if (!selectedCompanyId) return;
    if (!hasPeriod) {
      actions?.setPeriod(defaultPeriod);
      return;
    }
    actions?.loadCashflow({ ...activePeriod, scenario });
  }, [
    actions,
    selectedCompanyId,
    hasPeriod,
    activePeriod.from,
    activePeriod.to,
    defaultPeriod,
    filters.legalEntityId,
    filters.projectKey,
    filters.costCenterId,
    scenario,
  ]);

  const months = cashflow?.months || [];
  const competenceSignature = months.map((month) => month.competence).join("|");
  // Um mês que saiu do recorte fecha o painel.
  useEffect(() => {
    if (selectedCompetence && !competenceSignature.split("|").includes(selectedCompetence)) setSelectedCompetence(null);
  }, [competenceSignature, selectedCompetence]);

  useEffect(() => {
    if (!selectedCompanyId || !selectedCompetence) return;
    const visible = months.some((month) => month.competence === selectedCompetence);
    if (visible) actions?.loadCashflowBreakdown(selectedCompetence, { scenario });
  }, [
    actions,
    selectedCompanyId,
    selectedCompetence,
    cashflow?.dataRevision,
    competenceSignature,
    scenario,
  ]);

  const breakdownMatches = cashflowBreakdown?.competence === selectedCompetence;
  const retry = () => {
    actions?.clearError();
    actions?.loadCashflow({ ...activePeriod, scenario });
  };
  const companyName = cashflow?.scope?.companyName || access.companyName || "Portfólio";
  const ready = cashflow && cashflow.status !== "no_data";
  const totals = useMemo(
    () => (ready ? cashflowTwelveMonths(months, referenceCompetence(refreshStatus, access.resolvedAt)) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ready, competenceSignature, cashflow?.dataRevision, refreshStatus?.referenceDate, access.resolvedAt],
  );
  const partialScope = access.completePortfolioScope === false;
  const showScope = ready && (cashflow.coverage?.representedLegalEntityCount > 1 || partialScope);

  const status = <>
    {error && <section className="director-state director-state--error" role="alert">
      <strong>{error.message || "Não foi possível carregar o fluxo de caixa."}</strong>
      {error.retryable && <button className="director-button" type="button" onClick={retry}>Tentar novamente</button>}
    </section>}
    {loading.cashflow && !cashflow && <section className="director-state" role="status">
      Carregando movimentos financeiros…
    </section>}
    {!loading.cashflow && cashflow?.status === "no_data" && <section className="director-state">
      Ainda não há movimentos para o período e o recorte selecionados.
    </section>}
  </>;

  return <DirectorPage
    title={SCREEN.label}
    question={SCREEN.question}
    eyebrow={`Diretoria · ${companyName}`}
    showProjectFilter={false}
    onPublished={retry}
    status={status}
    qualitySources={ready ? [cashflow] : []}
    warnings={ready ? [
      ...readingWarnings({ refreshStatus, scenario }),
      showScope && <ScopeNotice
        key="scope"
        coverage={cashflow.coverage}
        completePortfolioScope={access.completePortfolioScope}
        partialPoolingGroupIds={access.partialPoolingGroupIds}
      />,
    ] : []}
    answer={ready ? cashflowAnswer(totals, { partialScope }) : null}
    cards={cashflowCards(totals, setSelectedCompetence)}
    cardsLabel="Resumo do movimento financeiro"
    chart={<>
      <p className="director-owner-footnote">{`${companyName} · ${formatCompetence(activePeriod.from)} a ${formatCompetence(activePeriod.to)}`}</p>
      <DirectorFilters
        metadata={metadata || {}}
        period={activePeriod}
        filters={filters}
        onPeriodChange={actions?.setPeriod}
        onFiltersChange={actions?.setFilters}
      />
      <section className="director-module1__filters" aria-label="Cenário da projeção mensal">
        <label className="director-filter-field"><span>Cenário</span><select value={scenario} onChange={(event) => setScenario(event.target.value)}>
          <option value="base">Base</option>
          <option value="conservative">Conservador</option>
          <option value="stress">Estresse</option>
        </select></label>
      </section>
      {ready && <CashflowMovementChart months={months} onSelectCompetence={setSelectedCompetence} />}
    </>}
    detail={ready ? <section className="director-section director-ledger" aria-labelledby="cashflow-ledger-title">
      <header>
        <div>
          <span className="director-eyebrow">Leitura por competência</span>
          <h2 id="cashflow-ledger-title">Movimentos mensais</h2>
        </div>
        <p>Clique num mês para ver a composição dele por obra e centro de custo.</p>
      </header>
      <CashflowTable
        months={months}
        selectedCompetence={selectedCompetence}
        onSelectCompetence={setSelectedCompetence}
      />
      {selectedCompetence && <DirectorSidePanel
        title={`Composição de ${formatCompetence(selectedCompetence)}`}
        onClose={() => setSelectedCompetence(null)}
      >
        <CashflowBreakdown
          competence={selectedCompetence}
          error={cashflowBreakdownError}
          items={breakdownMatches ? cashflowBreakdown.items : []}
          loading={Boolean(loading.cashflowBreakdown) || (
            !breakdownMatches
            && Boolean(selectedCompetence)
            && !cashflowBreakdownError
          )}
          onRetry={() => actions?.loadCashflowBreakdown(selectedCompetence, { scenario })}
        />
      </DirectorSidePanel>}
    </section> : null}
    notes={ready ? <>
      <p>
        <strong>Regime A · fluxo sem saldo.</strong> A projeção soma entradas e saídas do período; não parte do saldo
        bancário, que não é declarado. O acumulado é a diferença entre entradas e saídas dentro do período.
      </p>
      <p>Os cartões cobrem 12 meses a partir de {formatCompetence(totals?.from ?? activePeriod.from)}; o gráfico e a tabela seguem o período escolhido.</p>
      <FreshnessFooter
        freshness={cashflow.freshness}
        prefix={`Calculado em ${formatDateTimeCompact(cashflow.calculatedAt)}.`}
      />
    </> : null}
  />;
};

export default CashFlow;
